import PubNub, { Status, StatusEvent, Subscription, SubscriptionSet } from 'pubnub';
import { getChannelsRequest } from '@shared/events/requests/getChannels.request';
import { ExternalEvent, ExternalEventSource } from '../eventGateway';

interface PubnubMessagePayload {
  event_type: string;
  message_type: string;
  version: string;

  [key: string]: unknown;
}

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const FORBIDDEN_CODE = 403;

const MAX_RETRY_DELAY_MS = 10000;
const BASE_RETRY_DELAY_MS = 500;
export default class PubnubClient implements ExternalEventSource {
  private pubnub: PubNub | null = null;
  private subscriptions: SubscriptionSet | null = null;
  private retryCount = 0;
  private connectionPromise: Promise<void> | null = null;

  constructor(private userId: string) {}

  async start(): Promise<void | Error> {
    if (this.connectionPromise) {
      return this.connectionPromise;
    }
    const credentials = await getChannelsRequest();
    this.pubnub = new PubNub({
      authKey: credentials.authKey,
      subscribeKey: credentials.subscribeKey,
      userId: this.userId,
    });

    this.subscriptions = this.pubnub.subscriptionSet({
      channels: credentials.channels,
    });
    this.connectionPromise = this.connect();
    return await this.connectionPromise;
  }

  private async connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      const listener = {
        status: async (statusEvent: Status | StatusEvent) => {
          if (statusEvent.category === 'PNConnectedCategory') {
            this.retryCount = 0;
            resolve();
          } else if ((statusEvent as Status).statusCode === FORBIDDEN_CODE) {
            try {
              if (this.retryCount >= 5) {
                throw new Error('Max retries reached');
              }

              const delay = Math.min(
                BASE_RETRY_DELAY_MS * Math.pow(2, this.retryCount - 1),
                MAX_RETRY_DELAY_MS
              );
              await wait(delay);

              const credentials = await getChannelsRequest();

              this.pubnub?.setToken(credentials.authKey);
              this.pubnub?.reconnect();

              this.retryCount += 1;
            } catch (error) {
              reject(error);
            }
          }
        },
      };
      this.pubnub?.addListener(listener);
      this.subscriptions!.subscribe();
    });
  }
  subscribe(callback: (message: ExternalEvent) => void) {
    this.pubnub?.addListener({
      message: function (event: Subscription.Message) {
        const {
          event_type,
          message_type: _,
          version: __,
          ...rest
        } = event.message as PubnubMessagePayload;
        callback({
          type: event_type,
          payload: rest,
        });
      },
    });
  }

  unsubscribe() {
    this.pubnub?.removeAllListeners();
    this.subscriptions?.unsubscribe();
  }
}
